import React, { useEffect, useState } from 'react';
import {
  GitBranch, Plus, Play, Trash2, X, ChevronRight, ChevronDown,
  Sparkles, Globe, Database, GitFork, Clock, CheckCircle, XCircle,
  RefreshCw, Edit2, MoreVertical,
} from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import PageHeader from '../../components/PageHeader';

// ── Constants ────────────────────────────────────────────────────────────────
const STEP_TYPES = [
  { key: 'ai_prompt',   label: 'AI Prompt',    icon: Sparkles,  color: 'text-[#E3066A]',   bg: 'bg-[#FFE9F3]',   border: 'border-[#FFB8D8]',   desc: 'Ask an AI to process data or generate content' },
  { key: 'crm_action',  label: 'CRM Action',   icon: Database,  color: 'text-[#2E844A]',  bg: 'bg-[#E3F3E8]',  border: 'border-[#9FD9B4]',  desc: 'Create or update leads, deals, contacts' },
  { key: 'rest_api',    label: 'REST API Call', icon: Globe,     color: 'text-[#0176D3]',   bg: 'bg-[#EAF4FE]',   border: 'border-[#B8DAF3]',   desc: 'Fetch or send data to any HTTP endpoint' },
  { key: 'condition',   label: 'Condition',    icon: GitFork,   color: 'text-[#0176D3]', bg: 'bg-[#EEF4FF]', border: 'border-[#C9C9C9]', desc: 'Branch the flow based on a value' },
  { key: 'delay',       label: 'Delay',        icon: Clock,     color: 'text-[#706E6B]',   bg: 'bg-[#F7F9FB]',   border: 'border-[#E5E5E5]',   desc: 'Wait for a specified number of milliseconds' },
];

const TRIGGER_TYPES = [
  { key: 'manual',            label: 'Manual (run by user)' },
  { key: 'new_lead',          label: 'New Lead created' },
  { key: 'deal_stage_change', label: 'Deal stage changed' },
];

const CRM_ACTIONS = [
  { key: 'create_lead',  label: 'Create Lead' },
  { key: 'update_lead',  label: 'Update Lead' },
  { key: 'create_deal',  label: 'Create Deal' },
];

const CONDITION_OPS = [
  { key: 'equals',   label: 'equals' },
  { key: 'contains', label: 'contains' },
  { key: 'gt',       label: '>' },
  { key: 'lt',       label: '<' },
  { key: 'exists',   label: 'exists' },
];

// ── Tiny helpers ─────────────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 8); }

function stepMeta(type) { return STEP_TYPES.find(s => s.key === type) || STEP_TYPES[0]; }

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#706E6B] mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', className = '' }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className={`w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] ${className}`} />
  );
}

function Sel({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
      {options.map(o => <option key={o.key || o.value} value={o.key || o.value}>{o.label}</option>)}
    </select>
  );
}

// ── Step config editors ──────────────────────────────────────────────────────
function AiPromptConfig({ cfg, onChange }) {
  const s = k => v => onChange({ ...cfg, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="System Prompt">
        <textarea value={cfg.systemPrompt || ''} onChange={e => s('systemPrompt')(e.target.value)} rows={2}
          placeholder="You are a helpful CRM assistant that extracts lead information."
          className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
      </Field>
      <Field label="User Prompt (use {{variable}} for context values)">
        <textarea value={cfg.prompt || ''} onChange={e => s('prompt')(e.target.value)} rows={3}
          placeholder="Analyze this text and extract name, email, and phone: {{inputs.text}}"
          className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] font-mono text-xs" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Max Tokens"><Input type="number" value={cfg.maxTokens || 500} onChange={s('maxTokens')} /></Field>
        <Field label="JSON Mode">
          <Sel value={cfg.jsonMode ? 'yes' : 'no'} onChange={v => s('jsonMode')(v === 'yes')}
            options={[{ key: 'no', label: 'No' }, { key: 'yes', label: 'Yes (return JSON)' }]} />
        </Field>
      </div>
      <p className="text-xs text-[#706E6B]">Output stored as <code>{'{{step_id.output}}'}</code> for use in later steps.</p>
    </div>
  );
}

function RestApiConfig({ cfg, onChange }) {
  const s = k => v => onChange({ ...cfg, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Method">
          <Sel value={cfg.method || 'GET'} onChange={s('method')}
            options={['GET','POST','PUT','PATCH','DELETE'].map(m => ({ key: m, label: m }))} />
        </Field>
        <Field label="URL" className="col-span-2">
          <Input value={cfg.url || ''} onChange={s('url')} placeholder="https://api.example.com/data" />
        </Field>
      </div>
      <Field label="Auth Type">
        <Sel value={cfg.authType || 'none'} onChange={s('authType')}
          options={[{ key: 'none', label: 'None' }, { key: 'bearer', label: 'Bearer Token' }, { key: 'header', label: 'Custom Header' }]} />
      </Field>
      {cfg.authType !== 'none' && cfg.authType && (
        <div className="grid grid-cols-2 gap-3">
          {cfg.authType === 'header' && <Field label="Header Name"><Input value={cfg.authKeyName || ''} onChange={s('authKeyName')} placeholder="X-Api-Key" /></Field>}
          <Field label="Token"><Input type="password" value={cfg.authValue || ''} onChange={s('authValue')} placeholder="secret" /></Field>
        </div>
      )}
      <Field label="Request Body (JSON, optional)">
        <textarea value={cfg.body || ''} onChange={e => s('body')(e.target.value)} rows={2}
          placeholder='{"key": "{{inputs.value}}"}'
          className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] font-mono text-xs" />
      </Field>
      <p className="text-xs text-[#706E6B]">Response stored as <code>{'{{step_id.output}}'}</code>.</p>
    </div>
  );
}

function CrmActionConfig({ cfg, onChange }) {
  const s = k => v => onChange({ ...cfg, [k]: v });
  const sField = k => v => onChange({ ...cfg, fields: { ...cfg.fields, [k]: v } });
  const action = cfg.action || 'create_lead';

  return (
    <div className="space-y-3">
      <Field label="Action">
        <Sel value={action} onChange={s('action')} options={CRM_ACTIONS.map(a => ({ key: a.key, label: a.label }))} />
      </Field>
      {action === 'update_lead' && (
        <Field label="Lead ID (use {{step_id.output.leadId}})">
          <Input value={cfg.leadId || ''} onChange={s('leadId')} placeholder="{{step_1.output.leadId}}" />
        </Field>
      )}
      {(action === 'create_lead' || action === 'update_lead') && (
        <>
          <Field label="Name"><Input value={cfg.fields?.name || ''} onChange={sField('name')} placeholder="{{inputs.name}}" /></Field>
          <Field label="Email"><Input value={cfg.fields?.email || ''} onChange={sField('email')} placeholder="{{inputs.email}}" /></Field>
          <Field label="Phone"><Input value={cfg.fields?.phone || ''} onChange={sField('phone')} placeholder="{{inputs.phone}}" /></Field>
          <Field label="Source"><Input value={cfg.fields?.source || ''} onChange={sField('source')} placeholder="agentic-flow" /></Field>
        </>
      )}
      {action === 'create_deal' && (
        <>
          <Field label="Title"><Input value={cfg.fields?.title || ''} onChange={sField('title')} placeholder="{{inputs.dealTitle}}" /></Field>
          <Field label="Value"><Input type="number" value={cfg.fields?.value || ''} onChange={sField('value')} placeholder="0" /></Field>
        </>
      )}
    </div>
  );
}

function ConditionConfig({ cfg, onChange }) {
  const s = k => v => onChange({ ...cfg, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Variable (e.g. step_1.output or inputs.score)">
        <Input value={cfg.variable || ''} onChange={s('variable')} placeholder="inputs.score" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Operator"><Sel value={cfg.operator || 'equals'} onChange={s('operator')} options={CONDITION_OPS.map(o => ({ key: o.key, label: o.label }))} /></Field>
        <Field label="Compare Value"><Input value={cfg.value || ''} onChange={s('value')} placeholder="expected_value" /></Field>
      </div>
      <p className="text-xs text-[#706E6B]">The flow continues with the next step. Branching UI coming soon.</p>
    </div>
  );
}

function DelayConfig({ cfg, onChange }) {
  return (
    <Field label="Delay (milliseconds, max 30000)">
      <Input type="number" value={cfg.ms || 1000} onChange={v => onChange({ ...cfg, ms: Number(v) })} />
    </Field>
  );
}

function StepConfigEditor({ step, onChange }) {
  const meta = stepMeta(step.type);
  const Icon = meta.icon;
  return (
    <div className={`rounded-lg border-2 ${meta.border} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-lg ${meta.bg} flex items-center justify-center`}>
          <Icon size={14} className={meta.color} />
        </div>
        <div className="flex-1">
          <input value={step.label || ''} onChange={e => onChange({ ...step, label: e.target.value })}
            placeholder={meta.label}
            className="text-sm font-semibold text-[#2E2E2E] bg-transparent border-none outline-none w-full" />
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} font-medium`}>{meta.label}</span>
      </div>
      {step.type === 'ai_prompt'  && <AiPromptConfig  cfg={step.config} onChange={c => onChange({ ...step, config: c })} />}
      {step.type === 'rest_api'   && <RestApiConfig   cfg={step.config} onChange={c => onChange({ ...step, config: c })} />}
      {step.type === 'crm_action' && <CrmActionConfig cfg={step.config} onChange={c => onChange({ ...step, config: c })} />}
      {step.type === 'condition'  && <ConditionConfig cfg={step.config} onChange={c => onChange({ ...step, config: c })} />}
      {step.type === 'delay'      && <DelayConfig     cfg={step.config} onChange={c => onChange({ ...step, config: c })} />}
    </div>
  );
}

// ── Run history ───────────────────────────────────────────────────────────────
function RunHistory({ flowId, onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get(`/bot/agentic-flows/${flowId}/runs`)
      .then(r => setRuns(r.data.data || []))
      .finally(() => setLoading(false));
  }, [flowId]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-2xl max-h-[100dvh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex-shrink-0">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">Run History</h2>
          <button onClick={onClose}><X size={18} className="text-[#706E6B]" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading && <p className="text-sm text-[#706E6B] text-center py-8">Loading…</p>}
          {!loading && runs.length === 0 && <p className="text-sm text-[#706E6B] text-center py-8">No runs yet</p>}
          {runs.map(run => (
            <div key={run._id} className="border border-[#F3F2F2] rounded-lg overflow-hidden">
              <button
                onClick={() => setExpanded(expanded === run._id ? null : run._id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-[#F7F9FB] text-left"
              >
                {run.status === 'success' ? <CheckCircle size={16} className="text-[#04844B] flex-shrink-0" />
                  : run.status === 'failed' ? <XCircle size={16} className="text-[#BA0517] flex-shrink-0" />
                    : <RefreshCw size={16} className="text-[#DD7A01] animate-spin flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2E2E2E]">
                    {new Date(run.startedAt).toLocaleString()}
                  </p>
                  {run.errorMessage && <p className="text-xs text-[#BA0517] truncate">{run.errorMessage}</p>}
                </div>
                <span className="text-xs text-[#706E6B]">{run.logs?.length || 0} steps</span>
                {expanded === run._id ? <ChevronDown size={14} className="text-[#706E6B]" /> : <ChevronRight size={14} className="text-[#706E6B]" />}
              </button>
              {expanded === run._id && (
                <div className="border-t border-[#F3F2F2] bg-[#F7F9FB] p-3 space-y-1.5">
                  {(run.logs || []).map((log, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      {log.status === 'ok' ? <CheckCircle size={12} className="text-[#04844B] mt-0.5 flex-shrink-0" />
                        : <XCircle size={12} className="text-[#BA0517] mt-0.5 flex-shrink-0" />}
                      <div className="min-w-0">
                        <span className="font-medium text-[#444444]">{log.stepLabel || log.stepId}</span>
                        <span className="text-[#706E6B] ml-1">({log.durationMs}ms)</span>
                        {log.error && <p className="text-[#BA0517] mt-0.5">{log.error}</p>}
                        {log.output && (
                          <pre className="text-[#706E6B] mt-0.5 whitespace-pre-wrap break-all max-h-20 overflow-y-auto">
                            {typeof log.output === 'string' ? log.output : JSON.stringify(log.output, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Run Now modal ─────────────────────────────────────────────────────────────
function RunModal({ flow, onClose, onRan }) {
  const [inputs, setInputs] = useState('{}');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    let parsed;
    try { parsed = JSON.parse(inputs); } catch { alertService.error('Invalid JSON', 'Check your inputs JSON'); return; }
    setRunning(true);
    try {
      const { data } = await api.post(`/bot/agentic-flows/${flow._id}/run`, { inputs: parsed });
      setResult(data.data);
      onRan();
    } catch (e) {
      alertService.error('Run failed', e.response?.data?.message || e.message);
    } finally { setRunning(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md max-h-[100dvh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">Run: {flow.name}</h2>
          <button onClick={onClose}><X size={18} className="text-[#706E6B]" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!result ? (
            <>
              <div>
                <label className="block text-xs font-medium text-[#706E6B] mb-1">Input Variables (JSON)</label>
                <textarea value={inputs} onChange={e => setInputs(e.target.value)} rows={5}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                <p className="text-xs text-[#706E6B] mt-1">Access in steps via <code>{'{{inputs.key}}'}</code></p>
              </div>
              <button onClick={run} disabled={running}
                className="w-full bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white h-9 rounded text-[13px] font-semibold flex items-center justify-center gap-2">
                {running ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                {running ? 'Running…' : 'Run Flow'}
              </button>
            </>
          ) : (
            <div>
              <div className={`flex items-center gap-2 mb-3 ${result.status === 'success' ? 'text-[#2E844A]' : 'text-[#BA0517]'}`}>
                {result.status === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
                <span className="font-semibold">{result.status === 'success' ? 'Flow completed successfully' : 'Flow failed'}</span>
              </div>
              {result.errorMessage && <p className="text-sm text-[#BA0517] mb-3">{result.errorMessage}</p>}
              <p className="text-xs text-[#706E6B] mb-2">{result.logs?.length || 0} steps executed</p>
              <button onClick={onClose} className="w-full border border-[#E5E5E5] rounded py-2 text-sm text-[#5C5C5C] hover:bg-[#F7F9FB]">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add Step picker ───────────────────────────────────────────────────────────
function AddStepPicker({ onAdd, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-sm max-h-[100dvh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">Add Step</h2>
          <button onClick={onClose}><X size={16} className="text-[#706E6B]" /></button>
        </div>
        <div className="p-3 space-y-2">
          {STEP_TYPES.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => { onAdd(t.key); onClose(); }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border border-[#F3F2F2] hover:${t.bg} text-left transition-colors`}>
                <div className={`w-8 h-8 rounded-lg ${t.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={16} className={t.color} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2E2E2E]">{t.label}</p>
                  <p className="text-xs text-[#706E6B]">{t.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Flow editor panel ─────────────────────────────────────────────────────────
function FlowEditor({ flow: initialFlow, onSaved, onClose }) {
  const isNew = !initialFlow._id;
  const [form, setForm] = useState({
    name:        initialFlow.name        || '',
    description: initialFlow.description || '',
    trigger:     initialFlow.trigger     || { type: 'manual', config: {} },
    steps:       initialFlow.steps       || [],
    isActive:    initialFlow.isActive    !== false,
  });
  const [saving, setSaving] = useState(false);
  const [showAddStep, setShowAddStep] = useState(false);

  const addStep = (type) => {
    const id = `step_${uid()}`;
    setForm(f => ({ ...f, steps: [...f.steps, { id, type, label: '', config: {} }] }));
  };

  const updateStep = (idx, step) => {
    setForm(f => { const steps = [...f.steps]; steps[idx] = step; return { ...f, steps }; });
  };

  const removeStep = (idx) => {
    setForm(f => ({ ...f, steps: f.steps.filter((_, i) => i !== idx) }));
  };

  const moveStep = (idx, dir) => {
    setForm(f => {
      const steps = [...f.steps];
      const target = idx + dir;
      if (target < 0 || target >= steps.length) return f;
      [steps[idx], steps[target]] = [steps[target], steps[idx]];
      return { ...f, steps };
    });
  };

  const save = async () => {
    if (!form.name.trim()) { alertService.error('Name required', 'Enter a flow name'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/bot/agentic-flows', form);
      } else {
        await api.put(`/bot/agentic-flows/${initialFlow._id}`, form);
      }
      alertService.success('Saved', `Flow "${form.name}" saved`);
      onSaved();
    } catch (e) {
      alertService.error('Save failed', e.response?.data?.message || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Editor header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] flex-shrink-0 bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#5C5C5C] text-sm">← Back</button>
          <span className="text-[#C9C9C9]">|</span>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Flow name…"
            className="text-base font-bold text-[#080707] bg-transparent border-none outline-none min-w-0 flex-1" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <label className="flex items-center gap-1.5 text-xs text-[#706E6B] cursor-pointer">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded" />
            Active
          </label>
          <button onClick={save} disabled={saving}
            className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-4 py-1.5 rounded text-[13px] font-semibold whitespace-nowrap">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Description */}
        <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Flow description (optional)…"
          className="w-full text-sm text-[#706E6B] bg-transparent border-none outline-none" />

        {/* Trigger */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-xs font-semibold text-[#706E6B] mb-2">Trigger</p>
          <Sel value={form.trigger.type} onChange={v => setForm(f => ({ ...f, trigger: { ...f.trigger, type: v } }))}
            options={TRIGGER_TYPES.map(t => ({ key: t.key, label: t.label }))} />
          {form.trigger.type === 'webhook' && (
            <p className="text-xs text-[#706E6B] mt-2">Webhook endpoint available after saving.</p>
          )}
        </div>

        {/* Steps */}
        <div>
          <p className="text-xs font-semibold text-[#706E6B] mb-3">Steps ({form.steps.length})</p>
          {form.steps.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-[#E5E5E5] rounded-lg text-[#706E6B] text-sm">
              No steps yet — add your first step below
            </div>
          )}
          <div className="space-y-3">
            {form.steps.map((step, idx) => (
              <div key={step.id} className="relative">
                <div className="absolute -left-2 top-4 flex flex-col gap-0.5 z-10">
                  <button onClick={() => moveStep(idx, -1)} disabled={idx === 0}
                    className="w-5 h-5 flex items-center justify-center text-[#C9C9C9] hover:text-[#706E6B] disabled:opacity-20 text-xs">▲</button>
                  <button onClick={() => moveStep(idx, 1)} disabled={idx === form.steps.length - 1}
                    className="w-5 h-5 flex items-center justify-center text-[#C9C9C9] hover:text-[#706E6B] disabled:opacity-20 text-xs">▼</button>
                </div>
                <div className="ml-4">
                  <StepConfigEditor step={step} onChange={s => updateStep(idx, s)} />
                </div>
                <button onClick={() => removeStep(idx)}
                  className="absolute top-3 right-3 p-1 text-[#C9C9C9] hover:text-[#BA0517]">
                  <X size={13} />
                </button>
                {idx < form.steps.length - 1 && (
                  <div className="flex items-center justify-center py-1">
                    <div className="w-px h-4 bg-[#E5E5E5]" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <button onClick={() => setShowAddStep(true)}
            className="mt-3 w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#E5E5E5] hover:border-[#0176D3] rounded-lg py-3 text-sm text-[#706E6B] hover:text-[#0176D3] transition-colors">
            <Plus size={15} /> Add Step
          </button>
        </div>
      </div>

      {showAddStep && <AddStepPicker onAdd={addStep} onClose={() => setShowAddStep(false)} />}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function AgenticFlowsPage() {
  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('agentic_mode');
  const [flows, setFlows]         = useState([]);
  const [editing, setEditing]     = useState(null); // null | {} | flow
  const [showRuns, setShowRuns]   = useState(null); // flowId
  const [showRun, setShowRun]     = useState(null); // flow object
  const [loading, setLoading]     = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/bot/agentic-flows');
      setFlows(data.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (hasAccess) load(); }, [hasAccess]);

  if (featureLoading) return null;

  if (!hasAccess) {
    return (
      <FeatureLockedPage
        title="Agentic Flows"
        subtitle="Build AI-powered automation flows that act on your CRM"
        icon={GitBranch}
        plan={plan}
        featureName="Agentic Mode"
        description="Upgrade to create flows that combine AI prompts, CRM actions, and REST API calls into automated pipelines."
        highlights={[
          { icon: Sparkles, label: 'AI Prompts', desc: 'Use GPT to process and generate data' },
          { icon: Database, label: 'CRM Actions', desc: 'Create leads, update deals automatically' },
          { icon: Globe, label: 'REST API Calls', desc: 'Connect any HTTP API as a flow step' },
        ]}
      />
    );
  }

  const deleteFlow = async (id) => {
    if (!(await alertService.confirm('Delete?', 'Delete this flow and all its run history?', 'danger', 'Delete'))) return;
    await api.delete(`/bot/agentic-flows/${id}`);
    alertService.success('Deleted', 'Flow deleted');
    load();
  };

  const runFlow = async (flow) => setShowRun(flow);

  const STATUS_COLORS = { success: 'text-[#2E844A] bg-[#E3F3E8]', failed: 'text-[#BA0517] bg-[#FDE7E9]', running: 'text-[#DD7A01] bg-[#FFF4E5]' };

  // Show editor
  if (editing !== null) {
    return (
      <div className="flex flex-col h-screen bg-[#F7F9FB]">
        <FlowEditor
          flow={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#F7F9FB]">
      {/* Header */}
      <div className="px-4 sm:px-8 pt-5 flex-shrink-0">
        <PageHeader
          title="Agentic Flows"
          subtitle="Build AI-powered automation flows that read, reason, and act on your CRM data."
          icon={GitBranch}
          actions={
            <button onClick={() => setEditing({ name: '', description: '', trigger: { type: 'manual', config: {} }, steps: [], isActive: true })}
              className="flex items-center gap-2 bg-[#0176D3] hover:bg-[#014486] text-white px-4 h-9 rounded text-[13px] font-semibold whitespace-nowrap">
              <Plus size={15} /> New Flow
            </button>
          }
        />
      </div>

      {/* Flow list */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-8 pb-8">
        {loading && <p className="text-sm text-[#706E6B] text-center py-16">Loading…</p>}
        {!loading && flows.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-[#EAF4FE] rounded-lg flex items-center justify-center mx-auto mb-4">
              <GitBranch size={28} className="text-[#0176D3]" />
            </div>
            <p className="font-semibold text-[#444444] mb-1">No flows yet</p>
            <p className="text-sm text-[#706E6B] mb-4">Create your first agentic flow to automate CRM tasks with AI.</p>
            <button onClick={() => setEditing({ name: '', description: '', trigger: { type: 'manual', config: {} }, steps: [], isActive: true })}
              className="bg-[#0176D3] hover:bg-[#014486] text-white px-5 py-2 rounded text-[13px] font-semibold inline-flex items-center gap-2 whitespace-nowrap">
              <Plus size={14} /> Create First Flow
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4">
          {flows.map(flow => (
            <div key={flow._id} className="bg-white rounded-lg border border-[#E5E5E5] p-5 flex items-start gap-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <div className="w-10 h-10 bg-[#EAF4FE] rounded-lg flex items-center justify-center flex-shrink-0">
                <GitBranch size={18} className="text-[#0176D3]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-[#080707] truncate">{flow.name}</h3>
                  {!flow.isActive && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#F3F2F2] text-[#706E6B]">Inactive</span>
                  )}
                  {flow.lastRunStatus && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[flow.lastRunStatus] || 'bg-[#F3F2F2] text-[#706E6B]'}`}>
                      {flow.lastRunStatus}
                    </span>
                  )}
                </div>
                {flow.description && <p className="text-sm text-[#706E6B] mt-0.5 truncate">{flow.description}</p>}
                <div className="flex items-center gap-3 mt-1.5 text-xs text-[#706E6B]">
                  <span>{flow.steps?.length || 0} step{flow.steps?.length !== 1 ? 's' : ''}</span>
                  <span>·</span>
                  <span>trigger: {TRIGGER_TYPES.find(t => t.key === flow.trigger?.type)?.label || flow.trigger?.type}</span>
                  {flow.runCount > 0 && <>
                    <span>·</span>
                    <span>{flow.runCount} run{flow.runCount !== 1 ? 's' : ''}</span>
                  </>}
                  {flow.lastRunAt && <>
                    <span>·</span>
                    <span>last: {new Date(flow.lastRunAt).toLocaleDateString()}</span>
                  </>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => runFlow(flow)} title="Run now"
                  className="p-2 text-[#706E6B] hover:text-[#2E844A] rounded-lg hover:bg-[#E3F3E8]">
                  <Play size={15} />
                </button>
                <button onClick={() => setShowRuns(flow._id)} title="View run history"
                  className="p-2 text-[#706E6B] hover:text-[#0176D3] rounded-lg hover:bg-[#EAF4FE]">
                  <RefreshCw size={15} />
                </button>
                <button onClick={() => setEditing(flow)} title="Edit"
                  className="p-2 text-[#706E6B] hover:text-[#444444] rounded-lg hover:bg-[#F3F2F2]">
                  <Edit2 size={15} />
                </button>
                <button onClick={() => deleteFlow(flow._id)} title="Delete"
                  className="p-2 text-[#706E6B] hover:text-[#BA0517] rounded-lg hover:bg-[#FDE7E9]">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showRuns && <RunHistory flowId={showRuns} onClose={() => setShowRuns(null)} />}
      {showRun  && <RunModal flow={showRun} onClose={() => setShowRun(null)} onRan={load} />}
    </div>
  );
}
